import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { ArrowLeft, RefreshCw, CalendarDays, BarChart3, Layers } from "lucide-react";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

// Endpoints
const ENDPOINT_AGG = "seed/agg";
const ENDPOINT_REEXPAND = "seed/re-expand";
const ENDPOINT_CATEGORY = "seed/category-share";

// Fixed colours
const COLORS = {
    Baidu: { backgroundColor: "rgba(37, 99, 235, 0.5)", borderColor: "rgb(37, 99, 235)" }, // blue
    Google: { backgroundColor: "rgba(22, 163, 74, 0.5)", borderColor: "rgb(22, 163, 74)" }, // green
};

// Category badge style
const CHILD_CAT_BADGE =
    "text-xs px-2 py-0.5 rounded-full border bg-purple-50 text-purple-800 border-purple-200";

const PARENT_CAT_BADGE =
    "text-xs px-2 py-0.5 rounded-full border bg-slate-100 text-slate-700 border-slate-200";

export default function SeedChart() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [openParent, setOpenParent] = useState(null);
    const [selectedSource, setSelectedSource] = useState(null); // null = all
    const pickSource = (s) => setSelectedSource((prev) => (prev === s ? null : s));
    const chartRef = useRef(null);

    const [catRows, setCatRows] = useState([]); // [{category, count, pct}]
    const [catDenom, setCatDenom] = useState(0);
    const [catLoading, setCatLoading] = useState(false);
    const [catReqId, setCatReqId] = useState(0);

    const melbDate = (iso) => {
        const tz = "Australia/Melbourne";
        const toIso = () =>
            new Intl.DateTimeFormat("en-CA", {
                timeZone: tz,
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            }).format(new Date()); // -> YYYY-MM-DD

        const isoStr = iso || toIso();
        const [y, m, d] = isoStr.split("-");
        return { iso: isoStr, display: `${d}-${m}-${y.slice(-2)}` };
    };

    const [startDate, setStartDate] = useState(() => melbDate().iso);
    const [endDate, setEndDate] = useState(() => melbDate().iso);
    const displayRange = useMemo(() => {
        const s = melbDate(startDate).display;
        const e = melbDate(endDate).display;
        return s === e ? s : `${s} ~ ${e}`;
    }, [startDate, endDate]);

    const buildTimeParams = () => ({ start: startDate, end: endDate });

    const fetchAgg = async (timeParams) => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(ENDPOINT_AGG, { params: timeParams });
            setRows(res.data?.data || []);
        } catch (e) {
            setError(e?.message || "Failed to load");
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const sourceParam = (s) => (s ? s.toLowerCase() : "all");
    const fetchCategoryShare = async (timeParams) => {
        const reqId = catReqId + 1;
        setCatReqId(reqId);
        setCatLoading(true);
        try {
            const res = await axiosInstance.get(ENDPOINT_CATEGORY, {
                params: { ...timeParams, source: sourceParam(selectedSource) },
            });
            // ignore stale responses
            if (reqId !== catReqId + 1) return;
            setCatRows(res.data?.data || []);
            setCatDenom(res.data?.meta?.denominator || 0);
        } catch (e) {
            if (reqId !== catReqId + 1) return;
            toast.error(e?.message || "Failed to load category share");
        } finally {
            if (reqId === catReqId + 1) setCatLoading(false);
        }
    };

    useEffect(() => {
        const params = buildTimeParams();
        fetchAgg(params);
        setOpenParent(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    useEffect(() => {
        const params = buildTimeParams();
        fetchCategoryShare(params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, selectedSource]);

    const parents = useMemo(
        () => Array.from(new Set(rows.map((r) => r.parent))).sort((a, b) => a.localeCompare(b)),
        [rows]
    );

    const parentCategoryByParent = useMemo(() => {
        const m = {};
        for (const r of rows) if (r.parent_category && !m[r.parent]) m[r.parent] = r.parent_category;
        return m;
    }, [rows]);

    // counts by parent-source, counting distinct child domains
    const countsByParentSource = useMemo(() => {
        const map = {};
        const seen = {};
        for (const r of rows) {
            const key = `${r.parent}__${r.source}`;
            if (!seen[key]) seen[key] = new Set();
            seen[key].add(r.seed);
        }
        for (const key in seen) {
            const [parent, source] = key.split("__");
            if (!map[parent]) map[parent] = {};
            map[parent][source] = seen[key].size;
        }
        return map;
    }, [rows]);

    const childCatCountsByParent = useMemo(() => {
        const out = {}; // { parent: { category: count } }
        const seen = new Set(); // dedupe by (parent, category, seed)
        for (const r of rows) {
            if (!r.category) continue;
            const k = `${r.parent}__${r.category}__${r.seed}`;
            if (seen.has(k)) continue;
            seen.add(k);
            out[r.parent] ??= {};
            out[r.parent][r.category] = (out[r.parent][r.category] || 0) + 1;
        }
        return out;
    }, [rows]);

    const totalChildren = useMemo(() => {
        const s = new Set(rows.map((r) => r.seed));
        return s.size;
    }, [rows]);

    const visibleTotals = useMemo(() => {
        const sets = { Baidu: new Set(), Google: new Set() };
        for (const r of rows) {
            if (selectedSource && r.source !== selectedSource) continue;
            sets[r.source]?.add(r.seed);
        }
        return {
            Baidu: sets.Baidu.size,
            Google: sets.Google.size,
        };
    }, [rows, selectedSource]);

    const sources = ["Baidu", "Google"];

    const chartData = useMemo(
        () => ({
            labels: parents,
            datasets: sources.map((s) => ({
                label: s,
                data: parents.map((p) => {
                    const v = countsByParentSource[p]?.[s] || 0;
                    return selectedSource && selectedSource !== s ? 0 : v;
                }),
                backgroundColor: COLORS[s].backgroundColor,
                borderColor: COLORS[s].borderColor,
                borderWidth: 1,
                stack: "sources",
            })),
        }),
        [parents, countsByParentSource, selectedSource]
    );

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { bottom: 20 } },
            plugins: {
                legend: { position: "top" },
                title: { display: true, text: `Child domains by source on ${displayRange}` },
                tooltip: {
                    filter: (ctx) => ctx.parsed.y > 0,
                    callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}` },
                },
                datalabels: {
                    display: (ctx) => {
                        const v = ctx.dataset.data[ctx.dataIndex];
                        return typeof v === "number" && v > 0;
                    },
                    formatter: (v) => v,
                },
            },
            scales: {
                x: {
                    stacked: true,
                    ticks: {
                        autoSkip: true,
                        autoSkipPadding: 8,
                        minRotation: 45,
                        maxRotation: 45,
                    },
                },
                y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
            },
        }),
        [displayRange]
    );

    // Collect all parent categories present in the current rows
    const parentCats = useMemo(() => {
        const s = new Set(rows.map((r) => r.parent_category).filter(Boolean));
        return Array.from(s).sort((a, b) => a.localeCompare(b));
    }, [rows]);

    // Build percentages by source within each parent category, plus totals
    const parentCatAgg = useMemo(() => {
        // byCatSrc[cat][src] = Set of distinct child domains (seeds)
        const byCatSrc = {};
        // denom[cat] = Set of all distinct seeds across all sources for that parent category
        const denom = {};

        for (const r of rows) {
            const cat = r.parent_category;
            if (!cat) continue;
            byCatSrc[cat] ??= { Baidu: new Set(), Google: new Set() };
            byCatSrc[cat][r.source]?.add(r.seed);
            (denom[cat] ??= new Set()).add(r.seed);
        }

        // Order categories by total size (descending) for readability
        const cats = [...parentCats].sort((a, b) => (denom[b]?.size || 0) - (denom[a]?.size || 0));

        const pctsBySrc = { Baidu: [], Google: [] };
        const countsBySrc = { Baidu: [], Google: [] };
        const totals = cats.map((c) => denom[c]?.size || 0);

        for (const cat of cats) {
            const total = (denom[cat]?.size || 0) || 1;
            for (const src of ["Baidu", "Google"]) {
                const cnt = byCatSrc[cat]?.[src]?.size || 0;
                countsBySrc[src].push(cnt);
                pctsBySrc[src].push((cnt * 100) / total);
            }
        }

        return { cats, pctsBySrc, countsBySrc, totals };
    }, [rows, parentCats]);

    const parentCatData = useMemo(
        () => ({
            labels: parentCatAgg.cats,
            datasets: ["Baidu", "Google"].map((src) => ({
                label: src,
                data: parentCatAgg.pctsBySrc[src],
                backgroundColor: COLORS[src].backgroundColor,
                borderColor: COLORS[src].borderColor,
                borderWidth: 1,
                stack: "byParentCat",
            })),
        }),
        [parentCatAgg]
    );

    const parentCatOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { position: "top" },
                title: { display: true, text: "Parent category → source share (100%)" },
                tooltip: {
                    callbacks: {
                        label: (ctx) => {
                            const i = ctx.dataIndex;
                            const src = ctx.dataset.label;
                            const pct = Number(ctx.raw ?? 0);
                            const cnt = parentCatAgg.countsBySrc[src][i] ?? 0;
                            const total = parentCatAgg.totals[i] ?? 0;
                            return `${src}: ${pct.toFixed(1)}% (${cnt}/${total})`;
                        },
                    },
                },
                datalabels: {
                    display: (ctx) => {
                        const dsIndex = ctx.datasetIndex;
                        const numDs = ctx.chart.data.datasets.length;
                        return dsIndex === numDs - 1 && (parentCatAgg.totals[ctx.dataIndex] || 0) > 0;
                    },
                    formatter: (_v, ctx) => {
                        const total = parentCatAgg.totals[ctx.dataIndex] ?? 0;
                        return `N=${total}`;
                    },
                    anchor: "end",
                    align: "end",
                    offset: 4,
                    clip: true,
                },
            },
            scales: {
                x: { stacked: true, ticks: { maxRotation: 30, autoSkip: true, autoSkipPadding: 8 } },
                y: { stacked: true, beginAtZero: true, max: 100, ticks: { callback: (v) => `${v}%` } },
            },
        }),
        [parentCatAgg]
    );

    const catLabels = useMemo(() => catRows.map((d) => d.category), [catRows]);

    const catChartData = useMemo(
        () => ({
            labels: catLabels,
            datasets: [
                {
                    label: "% of distinct domains",
                    data: catRows.map((d) => d.pct),
                    backgroundColor: "rgba(196, 181, 253, 0.45)",
                    borderColor: "rgb(147, 51, 234)",
                    borderWidth: 1,
                },
            ],
        }),
        [catRows, catLabels]
    );

    const catChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                title: {
                    display: true,
                    text: `Child category share on ${displayRange} (${selectedSource || "All sources"}) • N=${catDenom}`,
                },
                tooltip: {
                    filter: (ctx) => Number.isFinite(ctx.raw) && ctx.raw > 0,
                    callbacks: {
                        label: (ctx) => `${(ctx.raw ?? 0).toFixed(1)}%`,
                    },
                },
                datalabels: {
                    display: (ctx) => {
                        const v =
                            (Array.isArray(ctx.dataset?.data) ? ctx.dataset.data[ctx.dataIndex] : undefined) ??
                            ctx.raw ??
                            (ctx.parsed ? ctx.parsed.y : undefined);
                        return Number.isFinite(v) && v > 0;
                    },
                    formatter: (v) => `${Number(v).toFixed(1)}%`,
                    anchor: "end",
                    align: "end",
                    clip: true,
                },
            },
            scales: {
                x: { ticks: { maxRotation: 30, minRotation: 0, autoSkip: true, autoSkipPadding: 8 } },
                y: { beginAtZero: true, max: 100, ticks: { callback: (v) => `${v}%` } },
            },
        }),
        [displayRange, selectedSource, catDenom]
    );

    const toggleParent = (parent) => {
        setOpenParent((prev) => (prev === parent ? null : parent));
    };

    const sendReexpand = async (parent, e) => {
        if (e) e.stopPropagation();
        try {
            const response = await axiosInstance.post(ENDPOINT_REEXPAND, { parent });
            if (response.status === 202) {
                toast.success(response.data.message || "Re-expand started");
            } else {
                toast.error("Error Re-expand");
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            toast.error("Error Re-expand: " + msg);
        }
    };

    const childrenByParent = useMemo(() => {
        const map = {};
        for (const r of rows) {
            if (!map[r.parent]) map[r.parent] = [];
            map[r.parent].push({ seed: r.seed, source: r.source, category: r.category });
        }
        for (const p in map) {
            const uniq = new Map();
            for (const item of map[p]) uniq.set(`${item.seed}__${item.source}`, item);
            map[p] = Array.from(uniq.values()).sort((a, b) => a.seed.localeCompare(b.seed));
        }
        return map;
    }, [rows]);

    const handleBarClick = (evt) => {
        const chart = chartRef.current;
        if (!chart) return;
        const els = chart.getElementsAtEventForMode(evt, "nearest", { intersect: true }, true);
        if (!els?.length) return;
        const { index } = els[0];
        const parent = chartData.labels[index];
        setOpenParent(parent);
        const el = document.getElementById(`parent-${parent}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const normalizeLinkedInUrl = (v) => {
        const s = String(v || "").trim();
        if (!s) return "";
        return s.startsWith("http") ? s : `https://${s}`;
    };

    // UI helpers (consistent style)
    const card =
        "rounded-2xl border border-slate-200 bg-white shadow-sm";
    const cardHeader =
        "flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3";
    const cardTitle =
        "flex items-center gap-2 text-sm font-semibold text-slate-800";
    const input =
        "h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/20";
    const btn =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:cursor-not-allowed disabled:opacity-60";
    const btnPrimary =
        "bg-slate-900 text-white hover:bg-slate-800";
    const btnGhost =
        "border border-slate-200 bg-white text-slate-800 hover:bg-slate-50";
    const chip =
        "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition";
    const dividerText =
        "text-xs text-slate-500";

    const sourcesChips = sources;

    return (
        <div className="min-h-screen bg-slate-50 p-4">
            <div className="mx-auto w-full max-w-7xl space-y-6">
                {/* Top bar */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
                            Seed Insight
                        </h1>
                        <p className="mt-1 text-sm text-slate-600">
                            Source mix, category share, and child domain breakdown for {displayRange}.
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => window.history.back()}
                            className={`${btn} ${btnGhost}`}
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                const params = buildTimeParams();
                                fetchAgg(params);
                                fetchCategoryShare(params);
                            }}
                            disabled={loading}
                            className={`${btn} ${btnPrimary}`}
                            title="Refresh charts and table"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>
                </div>

                {/* Error banner */}
                {error && (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        {String(error)}
                    </div>
                )}

                {/* Controls */}
                <div className={`${card} p-4`}>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-xl">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-slate-700">
                                    Start <span className="ml-1 font-medium text-slate-500">({melbDate(startDate).display})</span>
                                </label>
                                <div className="relative">
                                    <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className={`${input} pl-10`}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-slate-700">
                                    End <span className="ml-1 font-medium text-slate-500">({melbDate(endDate).display})</span>
                                </label>
                                <div className="relative">
                                    <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className={`${input} pl-10`}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <div className="mr-1 text-xs font-semibold text-slate-600">Source focus</div>
                            {sourcesChips.map((s) => {
                                const picked = selectedSource === s;
                                const active = selectedSource === s || selectedSource === null;
                                return (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => pickSource(s)}
                                        className={[
                                            chip,
                                            picked
                                                ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                                                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                                        ].join(" ")}
                                        title={picked ? "Click to show all sources" : `Show only ${s}`}
                                    >
                                        <span
                                            className="h-2.5 w-2.5 rounded-full"
                                            style={{ backgroundColor: COLORS[s].borderColor }}
                                        />
                                        <span className="font-semibold">{s}</span>
                                        {active && (
                                            <span className="ml-1 text-xs opacity-80">({visibleTotals[s]})</span>
                                        )}
                                    </button>
                                );
                            })}
                            <div className="ml-1 text-xs text-slate-500">
                                {selectedSource ? `Filtered to ${selectedSource}` : "All sources"}
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                            Parents: <span className="font-semibold text-slate-900">{parents.length}</span>
                        </span>
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                            Children: <span className="font-semibold text-slate-900">{totalChildren}</span>
                        </span>
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                            Baidu: <span className="font-semibold text-slate-900">{visibleTotals.Baidu}</span>
                        </span>
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                            Google: <span className="font-semibold text-slate-900">{visibleTotals.Google}</span>
                        </span>
                        {catLoading && (
                            <span className="ml-auto inline-flex items-center gap-2 text-slate-500">
                                <span className="h-2 w-2 animate-pulse rounded-full bg-slate-400" />
                                Updating category share…
                            </span>
                        )}
                    </div>
                </div>

                {/* Parent-category 100% stacked bar */}
                <div className={card}>
                    <div className={cardHeader}>
                        <div className={cardTitle}>
                            <Layers className="h-4 w-4 text-slate-500" />
                            Parent category → source share (100%)
                        </div>
                        <div className={dividerText}>Percent distribution per parent category</div>
                    </div>
                    <div className="p-4">
                        <div className="w-full" style={{ minHeight: 420 }}>
                            <Bar data={parentCatData} options={parentCatOptions} />
                        </div>
                    </div>
                </div>

                {/* Category % chart */}
                <div className={card}>
                    <div className={cardHeader}>
                        <div className={cardTitle}>
                            <BarChart3 className="h-4 w-4 text-slate-500" />
                            Child category share
                        </div>
                        <div className={dividerText}>
                            {selectedSource ? `${selectedSource} only` : "All sources"} • N={catDenom}
                        </div>
                    </div>
                    <div className="p-4">
                        <div className="relative w-full" style={{ minHeight: 380 }}>
                            <Bar data={catChartData} options={catChartOptions} />
                        </div>
                    </div>
                </div>

                {/* Main stacked chart */}
                <div className={card}>
                    <div className={cardHeader}>
                        <div className={cardTitle}>
                            <BarChart3 className="h-4 w-4 text-slate-500" />
                            Child domains by source
                        </div>
                        <div className={dividerText}>Click a bar to jump to that parent</div>
                    </div>
                    <div className="p-4">
                        <div className="w-full" style={{ minHeight: 550 }}>
                            <Bar ref={chartRef} data={chartData} options={chartOptions} onClick={handleBarClick} />
                        </div>
                    </div>
                </div>

                {/* Accordion */}
                <div className={`${card} overflow-hidden`}>
                    <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                        <span className="font-semibold">{parents.length}</span> parent domains,{" "}
                        <span className="font-semibold">{totalChildren}</span> child domains on{" "}
                        <span className="font-semibold">{displayRange}</span> —{" "}
                        <span className="text-slate-600">
                            Baidu: {visibleTotals.Baidu} · Google: {visibleTotals.Google}
                        </span>
                    </div>

                    <div className="divide-y divide-slate-200">
                        {parents.map((p) => {
                            const total = sources.reduce((acc, s) => acc + (countsByParentSource[p]?.[s] || 0), 0);
                            const parentCat = parentCategoryByParent[p];

                            return (
                                <div key={p} id={`parent-${p}`}>
                                    <button
                                        onClick={() => toggleParent(p)}
                                        className="w-full text-left px-4 py-3 flex items-start justify-between gap-4 transition hover:bg-slate-50"
                                    >
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <a
                                                    href={`//${p}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="font-semibold text-slate-900 hover:underline break-all"
                                                >
                                                    {p}
                                                </a>

                                                {parentCat && <span className={PARENT_CAT_BADGE}>{parentCat}</span>}

                                                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs text-slate-600">
                                                    {total} children
                                                </span>
                                            </div>

                                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                                {sources.map((s) => {
                                                    const c = countsByParentSource[p]?.[s] || 0;
                                                    if (!c) return null;

                                                    const color = s === "Google" ? "bg-emerald-600" : "bg-blue-600";

                                                    return (
                                                        <span
                                                            key={s}
                                                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white ${color}`}
                                                        >
                                                            {s}: {c}
                                                        </span>
                                                    );
                                                })}

                                                {Object.entries(childCatCountsByParent[p] || {})
                                                    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
                                                    .map(([cat, cnt]) => (
                                                        <span key={cat} className={CHILD_CAT_BADGE}>
                                                            {cat}: {cnt}
                                                        </span>
                                                    ))}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            {/* Kept commented to preserve functionality expectations */}
                                            {/* <button
                        onClick={(e) => sendReexpand(p, e)}
                        className="text-sm px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50"
                        title="Send to backend"
                      >
                        Re Expand
                      </button> */}

                                            <svg
                                                className={`h-5 w-5 text-slate-500 transition-transform ${openParent === p ? "rotate-180" : ""
                                                    }`}
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.08 1.04l-4.25 4.25a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                    </button>

                                    {openParent === p && (
                                        <div className="px-4 pb-4">
                                            <div className="mt-1 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                                                <ul className="space-y-2">
                                                    {(childrenByParent[p] || []).map(({ seed, source, category }) => (
                                                        <li
                                                            key={`${seed}__${source}`}
                                                            className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm"
                                                        >
                                                            <div className="min-w-0">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <a
                                                                        href={`//${seed}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="text-sm font-semibold text-slate-900 hover:underline break-all"
                                                                    >
                                                                        {seed}
                                                                    </a>

                                                                    <span
                                                                        className={[
                                                                            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-white",
                                                                            source === "Google"
                                                                                ? "bg-emerald-600"
                                                                            : "bg-blue-600",
                                                                        ].join(" ")}
                                                                    >
                                                                        {source}
                                                                    </span>

                                                                    {category ? <span className={CHILD_CAT_BADGE}>{category}</span> : null}
                                                                </div>
                                                            </div>
                                                        </li>
                                                    ))}

                                                    {!((childrenByParent[p] || []).length) && (
                                                        <li className="text-sm text-slate-500">No children</li>
                                                    )}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Note: unused helper kept for parity if you later need it */}
                {/* eslint-disable-next-line no-unused-vars */}
                <div className="hidden">{normalizeLinkedInUrl("")}</div>
            </div>
        </div>
    );
}
